/**
 * Der Agenten-Dialog: Name, Endpunkt, Preset-Kennung, Parameter, Kontextgrenze,
 * Prompt-Vorlagen und Zustand.
 *
 * Die Preset-Auswahl kommt aus der Modellliste des gewählten Endpunkts und nicht
 * aus einem Eingabefeld — Kennungen werden nicht abgetippt und nicht erfunden.
 * Angeboten werden **Kennungen**, keine Anzeigenamen: Die API verlangt
 * `api-devtool-qwen36-27b`, nicht `Api-Devtool-Qwen36-27b` (ADR-0005).
 *
 * Ein geparkter Agent steht mit seinem Grund in der Liste und wird ausdrücklich
 * freigegeben. Geparkt hat ihn der Arbeiter, weil das aufgelöste Basismodell vom
 * zuletzt gesehenen abwich; mit dem Modell ändert sich in aller Regel auch das
 * Kontextfenster, und eine von Hand gesetzte Token-Grenze wird dabei falsch.
 *
 * Stillgelegt wird ein Agent, gelöscht nur, solange kein **Werk** an ihm hängt —
 * kein Befund, kein Modellaufruf, keine Begutachtung, keine Zuordnung, keine
 * Widerlegung. Seine **Prüfaufträge** halten ihn nicht fest: Sie sind
 * Arbeitseinheiten und kein Werk und gehen mit ihm unter, der gerade laufende
 * ausgenommen. Beides entscheidet der Server, und seine Absage nennt den Weg.
 *
 * Dazu das **rückwirkende Nachholen** (Spec v1.3, Story 232): Prüfaufträge ab
 * einer wählbaren Startrevision, der billigste Weg, einen neuen Agenten gegen
 * einen bekannten Bestand zu beurteilen. Es steht hier und nicht an der
 * Hauptansicht, weil es Modellaufrufe kostet — und es geht in die **Abdeckung**
 * ein: Jede so eingereihte Revision ist ab dem Druck zuständig (ADR-0021).
 */

import { useEffect, useState } from "react";
import type { ReactElement } from "react";

import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  Dialog,
  FormGrid,
  FormRow,
  IconButton,
  Modal,
  Select,
  StatusPill,
  TextField,
} from "../../design-system";
import type {
  Agent,
  AgentDraft,
  AgentParameters,
  AdminApi,
  Endpoint,
  Preset,
} from "../../api/client";
import { formatNumber } from "../../formatting";
import { NumericCell, StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";

interface Roster {
  agents: Agent[];
  endpoints: Endpoint[];
}

export function AgentsSection({ client }: { client: AdminApi }): ReactElement {
  const { state, reload } = useLoaded<Roster>(async () => ({
    agents: await client.agents(),
    endpoints: await client.endpoints(),
  }));
  const [edited, setEdited] = useState<Agent | "neu" | null>(null);
  const [deleted, setDeleted] = useState<Agent | null>(null);
  const [backfilled, setBackfilled] = useState<Agent | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /** Was das letzte rückwirkende Nachholen erzeugt hat — eine Auskunft, keine Störung. */
  const [note, setNote] = useState<string | null>(null);

  const act = (action: Promise<unknown>): void => {
    setFailure(null);
    action.then(reload, (error: unknown) => setFailure(describe(error)));
  };

  return (
    <>
      <ListSection
        title="Agenten"
        glyph="bot"
        first
        headerRight={
          // Der Knopf zum Anlegen stand am Fuß der Karte; ein Abschnitt hat
          // keinen Fuß, und ein Schalter über die ganze Fensterbreite wäre
          // keiner. Er steht deshalb neben dem Abzeichen im Kopf.
          <span style={{ display: "flex", alignItems: "center", gap: "var(--sp-5)" }}>
            {state.kind === "loaded" ? (
              <Chip>{`${state.value.agents.length} angelegt`}</Chip>
            ) : null}
            <Button variant="outline" icon="plus" onClick={() => setEdited("neu")}>
              Agenten anlegen
            </Button>
          </span>
        }
      >
        <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {failure === null ? null : (
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          )}
          {note === null ? null : <Notice text={note} glyph="list-plus" />}
          {state.kind === "loaded" && state.value.agents.length === 0 ? (
            <Notice text="Kein Agent angelegt." />
          ) : null}
          {/* Ohne einen beauftragten Agenten entsteht keine Synthese und damit
              keine Review-Empfehlung (#79) — das ist ein zulässiger Zustand,
              aber einer, den man hier sehen muss. */}
          {state.kind === "loaded" &&
          state.value.agents.length > 0 &&
          !state.value.agents.some((agent) => agent.synthesisAgent) ? (
            <Notice
              text="Kein Agent ist mit der Synthese beauftragt — es entstehen keine Review-Empfehlungen."
              glyph="triangle-alert"
            />
          ) : null}
        </div>

        {state.kind === "loaded" && state.value.agents.length > 0 ? (
          <DataTable
            columns={[
              { key: "name", label: "Name", width: 200, maxWidth: 200 },
              { key: "endpoint", label: "Endpunkt", width: 140, maxWidth: 140 },
              // Eine Preset-Kennung wird abgetippt nachgelesen und nicht
              // überflogen: Sie bekommt die breiteste Textspalte und steht ganz
              // im Titel der Zelle.
              { key: "preset", label: "Preset", width: 300, maxWidth: 300 },
              { key: "limit", label: "Kontextgrenze", align: "right", width: 130 },
              { key: "synthesis", label: "Synthese", width: 110 },
              { key: "state", label: "Zustand", width: 210 },
              { key: "actions", label: "", width: 90, align: "right" },
            ]}
            rows={state.value.agents.map((agent) => ({
              cells: {
                name: <StrongCell>{agent.name}</StrongCell>,
                endpoint:
                  state.value.endpoints.find((one) => one.id === agent.endpointId)?.name ??
                  String(agent.endpointId),
                preset: <span title={agent.presetIdentifier}>{agent.presetIdentifier}</span>,
                limit: <NumericCell>{agent.contextLimit}</NumericCell>,
                synthesis: agent.synthesisAgent ? (
                  <StatusPill tone="green">beauftragt</StatusPill>
                ) : null,
                state: <StateCell agent={agent} />,
                actions: (
                  <span style={{ display: "inline-flex", gap: "var(--sp-3)" }}>
                    {agent.state === "parked" ? (
                      <IconButton
                        icon="lock-open"
                        tip="Freigeben"
                        size="sm"
                        tone="quiet"
                        onClick={() => act(client.releaseAgent(agent.id))}
                      />
                    ) : null}
                    {/* Ein stillgelegter Agent begutachtet nicht — der Server
                        weist das ohnehin ab, und ein Knopf, der nur eine
                        Fehlermeldung erzeugt, ist kein Angebot. */}
                    {agent.state === "retired" ? null : (
                      <IconButton
                        icon="history"
                        tip="Rückwirkend nachholen"
                        size="sm"
                        tone="quiet"
                        onClick={() => setBackfilled(agent)}
                      />
                    )}
                    <IconButton
                      icon="pencil"
                      tip="Ändern"
                      size="sm"
                      tone="quiet"
                      onClick={() => setEdited(agent)}
                    />
                    <IconButton
                      icon="trash-2"
                      tip="Löschen"
                      size="sm"
                      tone="quiet"
                      onClick={() => setDeleted(agent)}
                    />
                  </span>
                ),
              },
            }))}
          />
        ) : null}
      </ListSection>

      {deleted === null ? null : (
        <Modal onClose={() => setDeleted(null)}>
          <Dialog
            title="Agenten löschen"
            description={`„${deleted.name}" wird endgültig gelöscht. Was er hervorgebracht hat, hält ihn fest: Solange Befunde, Modellaufrufe, Begutachtungen, Zuordnungen oder Widerlegungen an ihm hängen, weist der Server das ab — dann bleibt das Stilllegen. Seine wartenden und aufgegebenen Prüfaufträge gehen mit ihm unter; läuft gerade einer, hilft Warten.`}
            width={480}
            onClose={() => setDeleted(null)}
            footer={
              <>
                <Button variant="outline" size="lg" onClick={() => setDeleted(null)}>
                  Abbrechen
                </Button>
                <Button
                  variant="danger"
                  size="lg"
                  onClick={() => {
                    act(client.deleteAgent(deleted.id));
                    setDeleted(null);
                  }}
                >
                  Endgültig löschen
                </Button>
              </>
            }
          />
        </Modal>
      )}

      {backfilled === null ? null : (
        <BackfillDialog
          agent={backfilled}
          backfill={(fromRevision) => client.backfillAgent(backfilled.id, fromRevision)}
          close={() => setBackfilled(null)}
          done={(enqueued, fromRevision) => {
            setBackfilled(null);
            setFailure(null);
            setNote(
              enqueued === 0
                ? `Ab Revision ${formatNumber(fromRevision)} gab es für „${backfilled.name}" nichts nachzuholen.`
                : `${formatNumber(enqueued)} Prüfaufträge für „${backfilled.name}" ab Revision ${formatNumber(fromRevision)} eingereiht.`,
            );
          }}
        />
      )}

      {edited === null || state.kind !== "loaded" ? null : (
        <AgentDialog
          agent={edited === "neu" ? null : edited}
          endpoints={state.value.endpoints}
          presetsOf={(id) => client.endpointModels(id).then((models) => models.presets)}
          save={(draft) =>
            edited === "neu" ? client.createAgent(draft) : client.updateAgent(edited.id, draft)
          }
          close={() => setEdited(null)}
          saved={() => {
            setEdited(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/** Der Zustand in der Liste. Ein geparkter Agent nennt seinen Grund, sonst wäre die Meldung keine. */
function StateCell({ agent }: { agent: Agent }): ReactElement {
  if (agent.state === "parked") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)" }}>
        <StatusPill tone="amber">geparkt</StatusPill>
        <span
          title={agent.parkedReason ?? ""}
          style={{
            fontSize: "var(--fs-11)",
            color: "var(--fg-6)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {agent.parkedReason ?? ""}
        </span>
      </span>
    );
  }

  return agent.state === "active" ? (
    <StatusPill tone="green">aktiv</StatusPill>
  ) : (
    <Chip>stillgelegt</Chip>
  );
}

/**
 * Das **rückwirkende Nachholen** (Spec v1.3, Story 232): Prüfaufträge für diesen
 * Agenten ab einer wählbaren Startrevision.
 *
 * Der billigste Weg, einen neu angelegten Agenten gegen einen bekannten Bestand
 * zu beurteilen, bevor er scharf geschaltet wird. Er kostet je Dateiänderung
 * einen Modellaufruf, und deshalb steht die Startrevision hier als bewusste
 * Eingabe und nicht als Vorbelegung: Wer sie zu tief setzt, zieht den halben
 * Bestand in die Warteschlange.
 *
 * **Es hebt die Abdeckung nicht auf Knopfdruck** (ADR-0021): Der Nenner hängt
 * daran, wozu der Agent einen Auftrag hat, und der Knopf kann ihn nur
 * vergrößern. Im Moment des Drucks fällt die Abdeckung deshalb, weil alles
 * wartet; sie steigt mit der geleisteten Arbeit. Das steht hier, weil es genau
 * die Erwartung ist, die dieser Knopf weckt.
 */
function BackfillDialog({
  agent,
  backfill,
  close,
  done,
}: {
  agent: Agent;
  backfill: (fromRevision: number) => Promise<number>;
  close: () => void;
  done: (enqueued: number, fromRevision: number) => void;
}): ReactElement {
  const [fromRevision, setFromRevision] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    const number = Number(fromRevision);

    if (!Number.isInteger(number) || number <= 0) {
      setError("Bitte eine Startrevision angeben.");

      return;
    }

    setBusy(true);
    setError(null);

    backfill(number).then(
      (enqueued) => done(enqueued, number),
      (failure: unknown) => {
        setBusy(false);
        setError(describe(failure));
      },
    );
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title="Rückwirkend nachholen"
        description={`Für „${agent.name}" entstehen Prüfaufträge zu jeder Dateiänderung ab der gewählten Revision. Das kostet je Dateiänderung einen Modellaufruf. Die Aufträge zählen in die Abdeckung: Sie fällt zunächst und steigt mit der abgearbeiteten Warteschlange.`}
        width={480}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird eingereiht ..." : "Nachholen"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Ab Revision" htmlFor="agent-from-revision">
            <TextField
              id="agent-from-revision"
              type="number"
              value={fromRevision}
              onChange={setFromRevision}
              placeholder="4700"
            />
          </FormRow>

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}

function AgentDialog({
  agent,
  endpoints,
  presetsOf,
  save,
  close,
  saved,
}: {
  agent: Agent | null;
  endpoints: Endpoint[];
  presetsOf: (endpointId: number) => Promise<Preset[]>;
  save: (draft: AgentDraft) => Promise<Agent>;
  close: () => void;
  saved: () => void;
}): ReactElement {
  const [name, setName] = useState(agent?.name ?? "");
  const [endpointId, setEndpointId] = useState<number | null>(agent?.endpointId ?? null);
  const [preset, setPreset] = useState(agent?.presetIdentifier ?? "");
  const [parameters, setParameters] = useState<[string, string][]>(
    Object.entries(agent?.parameters ?? {}).map(([key, value]) => [key, String(value)]),
  );
  const [retired, setRetired] = useState(agent?.state === "retired");
  const [contextLimit, setContextLimit] = useState(String(agent?.contextLimit ?? ""));
  const [refutationEnabled, setRefutationEnabled] = useState(agent?.refutationEnabled ?? false);
  const [synthesisAgent, setSynthesisAgent] = useState(agent?.synthesisAgent ?? false);
  const [reasoning, setReasoning] = useState(agent?.reasoning ?? false);
  const [reviewPrompt, setReviewPrompt] = useState(agent?.reviewPromptTemplate ?? "");
  const [synthesisPrompt, setSynthesisPrompt] = useState(agent?.synthesisPromptTemplate ?? "");
  const [presets, setPresets] = useState<Preset[] | null>(null);
  const [presetFailure, setPresetFailure] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Die Presets kommen vom gewählten Endpunkt. Wechselt der Endpunkt, wird die
  // Liste neu geholt — eine Kennung des einen Servers gilt am anderen nicht.
  useEffect(() => {
    if (endpointId === null) {
      setPresets(null);

      return;
    }

    let discarded = false;
    setPresets(null);
    setPresetFailure(null);

    presetsOf(endpointId).then(
      (found) => {
        if (!discarded) {
          setPresets(found);
        }
      },
      (failure: unknown) => {
        if (!discarded) {
          setPresetFailure(describe(failure));
        }
      },
    );

    return () => {
      discarded = true;
    };
  }, [endpointId]);

  const submit = (): void => {
    if (endpointId === null) {
      setError("Bitte einen Endpunkt wählen.");

      return;
    }

    setBusy(true);
    setError(null);

    save({
      name,
      endpointId,
      presetIdentifier: preset,
      parameters: toParameters(parameters),
      contextLimit: Number(contextLimit),
      reviewPromptTemplate: reviewPrompt,
      synthesisPromptTemplate: synthesisPrompt,
      refutationEnabled,
      synthesisAgent,
      reasoning,
      // `parked` steht nicht zur Wahl: Der Weg zurück ist die Freigabe, und ein
      // geparkter Agent behält seinen Zustand, auch wenn hier etwas geändert wird.
      ...(agent === null || agent.state === "parked"
        ? {}
        : { state: retired ? ("retired" as const) : ("active" as const) }),
    }).then(saved, (failure: unknown) => {
      setBusy(false);
      setError(describe(failure));
    });
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title={agent === null ? "Agenten anlegen" : "Agenten ändern"}
        description="Gerufen wird das Basismodell hinter dem Preset — deshalb sind die Parameter hier wirksam und werden mit den Befunden versioniert."
        width={640}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird gespeichert ..." : "Speichern"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Name" htmlFor="agent-name">
            <TextField
              id="agent-name"
              value={name}
              onChange={setName}
              placeholder="Qwen, streng"
            />
          </FormRow>

          <FormRow label="Endpunkt">
            <Select
              options={endpoints.map((one) => one.name)}
              value={endpoints.find((one) => one.id === endpointId)?.name ?? ""}
              placeholder="Endpunkt wählen ..."
              onSelect={(chosen) =>
                setEndpointId(endpoints.find((one) => one.name === chosen)?.id ?? null)
              }
            />
          </FormRow>

          <FormRow label="Preset">
            <Select
              options={(presets ?? []).map((one) => one.identifier)}
              value={preset}
              placeholder={
                endpointId === null
                  ? "Erst den Endpunkt wählen ..."
                  : presets === null
                    ? "Wird geladen ..."
                    : "Preset wählen ..."
              }
              disabled={presets === null || presets.length === 0}
              onSelect={setPreset}
            />
          </FormRow>

          {presetFailure === null ? null : (
            <FormRow full>
              <Notice text={presetFailure} glyph="unplug" color="var(--ac-red)" />
            </FormRow>
          )}

          <FormRow label="Kontextgrenze" htmlFor="agent-limit">
            <TextField
              id="agent-limit"
              type="number"
              value={contextLimit}
              onChange={setContextLimit}
              placeholder="120000"
            />
          </FormRow>

          <FormRow label="Denkmodus">
            <Checkbox
              label="Mit Denkmodus (enable_thinking)"
              checked={reasoning}
              onChange={setReasoning}
            />
          </FormRow>

          <FormRow label="">
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              Gemessen dauert ein Aufruf mit Denkmodus rund viermal so lange wie ohne — je Aufruf 136-139
              s statt 28-29 s.
            </span>
          </FormRow>

          <ParameterRows rows={parameters} onChange={setParameters} />

          <PromptField
            id="agent-review-prompt"
            label="Vorlage Begutachtung"
            value={reviewPrompt}
            onChange={setReviewPrompt}
          />

          <PromptField
            id="agent-synthesis-prompt"
            label="Vorlage Synthese"
            value={synthesisPrompt}
            onChange={setSynthesisPrompt}
          />

          <FormRow label="Widerlegung">
            <Checkbox
              label="Befunde dieses Agenten widerlegen lassen"
              checked={refutationEnabled}
              onChange={setRefutationEnabled}
            />
          </FormRow>

          <FormRow label="">
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              Ein anderer Agent prüft dann jeden Befund, den nur dieser hier trägt, gegen — das
              kostet je Befund einen weiteren Modellaufruf.
            </span>
          </FormRow>

          <FormRow label="Synthese">
            <Checkbox
              label="Mit der Synthese beauftragt"
              checked={synthesisAgent}
              onChange={setSynthesisAgent}
            />
          </FormRow>

          <FormRow label="">
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              Höchstens ein Agent trägt diese Beauftragung. Er führt die Befunde aller Agenten zur
              einen Review-Empfehlung einer Revision zusammen — steht schon ein anderer, weist der
              Server das Speichern ab.
            </span>
          </FormRow>

          {/* Stillgelegt statt gelöscht: Die bisherigen Befunde und Aufrufe eines
              Agenten bleiben erhalten. Ein geparkter Agent steht hier nicht zur
              Wahl — ihn gibt der Knopf in der Liste frei. */}
          {agent === null || agent.state === "parked" ? null : (
            <FormRow label="Zustand">
              <Select
                options={["aktiv", "stillgelegt"]}
                value={retired ? "stillgelegt" : "aktiv"}
                onSelect={(chosen) => setRetired(chosen === "stillgelegt")}
              />
            </FormRow>
          )}

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}

/**
 * Die Parameter als Paare aus Name und Wert.
 *
 * Welche ein Endpunkt versteht, weiß er selbst — die Maske gibt deshalb keine
 * feste Liste vor, sondern nur die Form. Temperatur und Token-Grenze sind die
 * beiden, die man immer setzt.
 *
 * Die Zeilen stehen unmittelbar im Raster der Maske und nicht in einem eigenen
 * Kasten: `FormRow` zeichnet mit `display: contents` nichts Eigenes, sondern legt
 * Beschriftung und Feld in die Spalten seines `FormGrid` — ein Kasten dazwischen
 * nähme sie aus dem Raster und die Felder von ihrer Kante.
 */
function ParameterRows({
  rows,
  onChange,
}: {
  rows: [string, string][];
  onChange: (rows: [string, string][]) => void;
}): ReactElement {
  return (
    <>
      <FormRow label="Parameter">
        <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
          Temperatur, Token-Grenze, weitere — je Zeile ein Name und ein Wert.
        </span>
      </FormRow>

      {rows.map((row, index) => (
        <FormRow key={index} label="">
          <span style={{ display: "flex", gap: "var(--sp-4)", alignItems: "center" }}>
            <TextField
              value={row[0]}
              placeholder="temperature"
              onChange={(typed) =>
                onChange(rows.map((one, at) => (at === index ? [typed, one[1]] : one)))
              }
            />
            <TextField
              value={row[1]}
              placeholder="0"
              onChange={(typed) =>
                onChange(rows.map((one, at) => (at === index ? [one[0], typed] : one)))
              }
            />
            <IconButton
              icon="trash-2"
              tip="Parameter entfernen"
              size="sm"
              tone="quiet"
              onClick={() => onChange(rows.filter((_, at) => at !== index))}
            />
          </span>
        </FormRow>
      ))}

      <FormRow label="">
        <span>
          <Button
            variant="outline"
            size="sm"
            icon="plus"
            onClick={() => onChange([...rows, ["", ""]])}
          >
            Parameter hinzufügen
          </Button>
        </span>
      </FormRow>
    </>
  );
}

/**
 * Eine Prompt-Vorlage. Mehrzeilig, deshalb ein Textfeld — einen mehrzeiligen
 * Baustein gibt es im Design-System nicht, und nachgebaut wird keiner; die Werte
 * für Fläche, Linie und Schrift kommen trotzdem aus den Tokens.
 */
function PromptField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}): ReactElement {
  return (
    <FormRow label={label} htmlFor={id}>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        style={{
          width: "100%",
          minHeight: 76,
          padding: "var(--sp-4) var(--sp-5)",
          background: "var(--surface-field)",
          border: "1px solid var(--border-field)",
          borderRadius: "var(--r-field)",
          color: "var(--fg-1)",
          fontFamily: "var(--font-sans)",
          fontSize: "var(--fs-12)",
          resize: "vertical",
          outline: "none",
        }}
      />
    </FormRow>
  );
}

/**
 * Aus den Zeilen werden die Parameter. Was wie eine Zahl aussieht, geht als Zahl
 * über den Draht, `true` und `false` als Wahrheitswert — ein Modell, das
 * `temperature: "0.2"` bekommt, weist die Anfrage zurück.
 */
function toParameters(rows: [string, string][]): AgentParameters {
  const parameters: AgentParameters = {};

  for (const [name, value] of rows) {
    if (name.trim() === "") {
      continue;
    }

    if (value === "true" || value === "false") {
      parameters[name] = value === "true";
    } else if (value.trim() !== "" && !Number.isNaN(Number(value))) {
      parameters[name] = Number(value);
    } else {
      parameters[name] = value;
    }
  }

  return parameters;
}
